/**
 * @module CopyProjectModal
 * @description A modal component for copying a project to a new owner with selected parts.
 */

import React, { useEffect, useState } from "react"
import { Modal, Input, Checkbox, Radio } from "antd"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"
import { CheckboxChangeEvent } from "antd/es/checkbox"
import { RadioChangeEvent } from "antd/es/radio"

import { CopyHistoryMode } from "api/Repos/projects"
import {
  copiedNotWaveId,
  useCopyProjectMutation,
} from "features/Projects/api/useProjectsMutations"
import { ProjectRef, useAddToTracker } from "features/Provenance"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useProjectStore } from "stores/projectStore"

// Types for copy options
interface CopyOptions {
  metadata: boolean
  qnr: boolean
  data: boolean
  docs: boolean
  queries: boolean
}

type CopyOptionKey = keyof CopyOptions

interface CopyOptionLabels {
  metadata: string
  qnr: string
  data: string
  docs: string
  queries: string
}

interface CopyProjectModalProps {
  userData: string[]
  /** When given, the parent opens the modal (from a menu) and no button is shown */
  open?: boolean
  /** Told when the modal opens or closes, for a parent that controls `open` */
  onOpenChange?: (open: boolean) => void
}

/**
 * @function CopyProjectModal
 * @description A modal component for copying a project to a new owner with selected parts.
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The CopyProjectModal component
 */
const CopyProjectModal: React.FC<CopyProjectModalProps> = ({
  userData,
  open,
  onOpenChange,
}) => {
  const project = useProjectStore((state) => state.project)
  const copyProjectMutation = useCopyProjectMutation()
  const navigate = useNavigate()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  // A parent that passes `open` owns the visibility; otherwise this does.
  const isVisible = open ?? isModalVisible
  const setVisible = (visible: boolean): void => {
    setIsModalVisible(visible)
    onOpenChange?.(visible)
  }
  const [displayUserData, setDisplayUserData] = useState<string[]>([])
  const [newProjectName, setNewProjectName] = useState<string>("")
  const [selectedOwner, setSelectedOwner] = useState<string>("")
  const [copyOptions, setCopyOptions] = useState<CopyOptions>({
    metadata: true,
    qnr: true,
    data: true,
    docs: true,
    queries: true,
  })
  // What happens to the copy's metadata version history (defaults to a clean copy).
  const [historyMode, setHistoryMode] = useState<CopyHistoryMode>("clean")
  // A wave copy that was made but couldn't be added to the tracker: offered to add it again
  const [unlinkedCopy, setUnlinkedCopy] = useState<ProjectRef | null>(null)
  const addToTracker = useAddToTracker()

  const copyOptionLabels: CopyOptionLabels = {
    metadata: "Project Info",
    qnr: "Questionnaire",
    data: "Data",
    docs: "Documents",
    queries: "Queries",
  }

  // Prepare user data with project creator first
  useEffect(() => {
    if (userData.length > 0) {
      const userArray = [
        project.Created_by,
        ...userData.filter(
          (user: string) =>
            user.toLowerCase() !== project.Created_by.toLowerCase()
        ),
      ]
      setDisplayUserData(userArray)
    }
  }, [userData, project.Created_by])

  /**
   * @async
   * @function copyProject
   * @description Copies the project with selected options to a new owner.
   * @returns {Promise<void>}
   */
  const copyProject = async (): Promise<void> => {
    if (!newProjectName.trim() || !selectedOwner) {
      notify.error(
        "Error Copying Project!",
        "Please enter a project name and select a new owner."
      )
      return
    }

    const copyParts: string = Object.entries(copyOptions)
      .filter(([, value]: [string, boolean]) => value)
      .map(([key]: [string, boolean]) => key)
      .join(",")

    try {
      await copyProjectMutation.mutateAsync({
        id: project.id,
        owner: project.Created_by,
        copy: copyParts,
        name: newProjectName.trim(),
        newOwner: selectedOwner,
        historyMode,
      })
      cancel() // Reset form and close modal
      navigate("/home")
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error copying project:", error)
      const copyId = copiedNotWaveId(error)
      if (copyId) setUnlinkedCopy({ id: copyId, owner: selectedOwner })
    }
  }

  /**
   * Adds the wave copy that couldn't be linked to this project's tracker, as the copy itself
   * would have been. A failure is reported by the mutation; the offer stays to try again.
   */
  const addCopyToTracker = (): void => {
    if (!unlinkedCopy) return
    addToTracker.mutate(
      { wave: unlinkedCopy, projectId: project.id },
      {
        onSuccess: () => {
          notify.toast.success("The copy was added to the tracker.")
          cancel()
          navigate("/home")
        },
      }
    )
  }

  /**
   * @function showModal
   * @description Sets the modal visibility to true.
   */
  const showModal = (): void => {
    setVisible(true)
  }

  /**
   * @function cancel
   * @description Resets the modal state and closes it.
   */
  const cancel = (): void => {
    setVisible(false)
    setNewProjectName("")
    setSelectedOwner("")
    setCopyOptions({
      metadata: true,
      qnr: true,
      data: true,
      docs: true,
      queries: true,
    })
    setHistoryMode("clean")
    setUnlinkedCopy(null)
  }

  /**
   * @function handleCopyOptionChange
   * @description Handles changes in the copy options checkboxes.
   * @param {CheckboxChangeEvent} e - The checkbox change event
   */
  const handleCopyOptionChange = (e: CheckboxChangeEvent): void => {
    const { name, checked } = e.target
    if (name && name in copyOptions) {
      setCopyOptions({
        ...copyOptions,
        [name as CopyOptionKey]: checked,
      })
    }
  }

  /**
   * @function handleOwnerChange
   * @description Handles changes in the owner selection.
   * @param {CheckboxChangeEvent} e - The checkbox change event
   */
  const handleOwnerChange = (e: CheckboxChangeEvent): void => {
    const { value, checked } = e.target
    setSelectedOwner(checked ? (value as string) : "")
  }

  /**
   * @function handleProjectNameChange
   * @description Handles changes in the project name input.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleProjectNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewProjectName(e.target.value)
  }

  /**
   * @function isFormValid
   * @description Checks if the form is valid for submission.
   * @returns {boolean} True if form is valid, false otherwise
   */
  const isFormValid = (): boolean => {
    return newProjectName.trim().length > 0 && selectedOwner.length > 0
  }

  return (
    <>
      {open === undefined && (
        <DataQButton onClick={showModal} type="primary">
          Copy Project
          <FontAwesomeIcon icon={faCopy} />
        </DataQButton>
      )}

      <Modal
        open={isVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
        width={600}
      >
        <h2 className="text-xl font-semibold m-0">Copy Project</h2>

        <Input
          placeholder="Enter new project name"
          value={newProjectName}
          onChange={handleProjectNameChange}
          className="my-4"
          maxLength={100}
        />

        <h3 className="text-lg font-medium m-0 mt-2">Select New Owner</h3>
        <div className="max-h-40 overflow-y-auto">
          {displayUserData.map((user: string) => (
            <div key={user} className="flex items-center mb-2">
              <Checkbox
                value={user}
                checked={selectedOwner === user}
                onChange={handleOwnerChange}
                className="mr-2"
              />
              <span>{user}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        <h3 className="text-lg font-medium mt-0 mb-2">Select Parts to Copy</h3>
        <div className="flex flex-wrap">
          {(Object.entries(copyOptions) as [CopyOptionKey, boolean][]).map(
            ([key, value]) => (
              <div key={key} className="w-1/2 mb-2 flex items-center">
                <Checkbox
                  name={key}
                  checked={value}
                  onChange={handleCopyOptionChange}
                  className="mr-2"
                />
                <span>{copyOptionLabels[key]}</span>
              </div>
            )
          )}
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        <h3 className="text-lg font-medium mt-0 mb-2">Version History</h3>
        <Radio.Group
          value={historyMode}
          onChange={(e: RadioChangeEvent) =>
            setHistoryMode(e.target.value as CopyHistoryMode)
          }
          className="flex flex-col gap-2"
        >
          <Radio value="clean">
            <span className="font-medium">Clean copy</span>
            <span className="block text-xs text-gray-500">
              Start fresh: none of the source&apos;s version history is carried over.
            </span>
          </Radio>
          <Radio value="full">
            <span className="font-medium">Full duplicate</span>
            <span className="block text-xs text-gray-500">
              Keep the source&apos;s version history and its saved versions.
            </span>
          </Radio>
          <Radio value="wave">
            <span className="font-medium">New tracker wave</span>
            <span className="block text-xs text-gray-500">
              Reset history, but link to the source as the next wave of a tracker.
            </span>
          </Radio>
        </Radio.Group>

        {unlinkedCopy && (
          <div
            role="status"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
          >
            The project was copied, but the copy isn&apos;t in this project&apos;s tracker yet.
            <div className="mt-2">
              <DataQButton
                type="primary"
                onClick={addCopyToTracker}
                isLoading={addToTracker.isPending}
                isDisabled={addToTracker.isPending}
              >
                Add the copy to the tracker
              </DataQButton>
            </div>
          </div>
        )}

        <div className="flex flex-row mt-5 justify-between">
          <DataQButton type="secondary" onClick={cancel}>
            {unlinkedCopy ? "Close" : "Cancel"}
          </DataQButton>
          {/* One solid button: Add the copy to the tracker takes it while that offer shows */}
          <DataQButton
            type={unlinkedCopy ? "secondary" : "primary"}
            onClick={copyProject}
            isLoading={copyProjectMutation.isPending}
            isDisabled={!isFormValid() || copyProjectMutation.isPending || !!unlinkedCopy}
          >
            Copy Project
          </DataQButton>
        </div>
      </Modal>
    </>
  )
}

export default CopyProjectModal
